import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PayerStatusFindInterface } from '@zaoblako/shared/modules/catalog/dtos/payer-status/payer-status-find.dto';
import {
  PayerStatusFormDto,
  PayerStatusFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/payer-status/payer-status-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { BasisPayerStatusService } from '@zaoblako/web/core/services/basis/basis-payer-status.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-payer-status-form',
  templateUrl: './payer-status-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    CheckboxModule,
    FormsModule,
    ButtonComponent,
    SpinnerComponent,
    TagModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PayerStatusFormComponent extends BaseFormComponent<PayerStatusFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public status = <PayerStatusFormInterface>{};
  public loading$: Observable<boolean>;

  public dto = PayerStatusFormDto;
  public form$: Observable<FormType>;

  public constructor(
    private readonly basisPayerStatusService: BasisPayerStatusService,
    public readonly formStoreService: FormStoreService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.basisPayerStatusService
      .findById<PayerStatusFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        withDeleted: true
      })
      .subscribe({
        next: (status) => {
          this.status = status;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveStatus(): void {
    if (this.checkValidate(this.status).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.basisPayerStatusService.update<PayerStatusFormInterface, PayerStatusFindInterface>(this.status).subscribe({
          next: (status) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Статус плательщика "${this.status.name}" обновлен!`);
            this.formStoreService.saveNext(status, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.basisPayerStatusService.create<PayerStatusFindInterface>(this.status).subscribe({
          next: (status) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Статус плательщика создан!');
            this.formStoreService.saveNext(status, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteStatus(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить статус плательщика "${this.status.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisPayerStatusService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Статус плательщика "${this.status.name}" удален!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreStatus(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить статус плательщика "${this.status?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisPayerStatusService.restore(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.status = { ...this.status, deleted: null } as PayerStatusFindInterface;
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Статус плательщика "${this.status?.name}" восстановлен!`);
            this.formStoreService.saveNext(this.status, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
